import { Fixture } from "./overview";
import React, { FC, useEffect, useState } from "react";
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCol,
    IonGrid,
    IonItem,
    IonRow,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { TFunction, useTranslation } from "react-i18next";
import {
    GpsStats,
    HrData,
    MatchPhone,
    OfficialRole,
    Sprint,
} from "refsix-js-models";
import "./stats.css";
import {
    DistanceCoveredComponent,
    HeartRateComponent,
    SpeedCategoryComponent,
} from "../../../components";
import { getPlayingSegments, GpsProcessingService } from "refsix-core";
import { useFitnessDataContext } from "../../../components/hooks";
import { GpsMap, GpsMapType } from "../../../components/GpsMapComponent";
import { ProTeaser } from "../../../components/ProTeaser";
import { hasFeatureAccess } from "../../../services/subscriptionService";
import exampleMatch from "../../../support/example-result.json";
import ScrollableContentWithAds from "../../../components/ScrollableContentsWithAd";
import { ScorzaCard } from "../../../components/stats/ScorzaCard";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { allMatches } from "../../../services/matchService";
import { MatchDebug } from "../../../components/debug/MatchDebug";
import FitnessDataItemWrapper from "../../../components/FitnessDataItemWrapper";
import { routes } from "../../../route/constants";

export function notStatsDesc(match: MatchPhone, t: TFunction) {
    return (
        <IonGrid>
            <IonRow className="match-detail-row">
                {!match.matchFinished && (
                    <div style={{ marginTop: 10 }}>
                        <IonText>{t("stats.stats.noData")}</IonText>
                    </div>
                )}
            </IonRow>
        </IonGrid>
    );
}

const Stats: FC<Fixture> = ({ match }) => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const heatMapAccess = hasFeatureAccess("heatmap");
    const sprintMapAccess = hasFeatureAccess("sprints");
    const heartRateAccess = hasFeatureAccess("heartRate");

    const playedSegments =
        (match.matchEvents && getPlayingSegments(match)) || [];
    const [calculatedStats, setCalculatedStats] = useState<GpsStats | {}>({});
    const [filteredHrData, setFilteredHrData] = useState<HrData>();
    const [sprintsData, setSprintsData] = useState<Sprint<number>[]>();

    const { gpsData, hrData, loading, reload } = useFitnessDataContext();
    const developerToolsEnabled = useSelector(
        (state: RootState) => state.developerTools.enabled
    );

    useEffect(() => {
        // Reload stats if match has gps or heart rate data, but hasTracking or hasHeartRate is false
        reload();
    }, []);

    useEffect(() => {
        if (match.matchFinished) {
            if (gpsData && gpsData.matchTimings) {
                const gpsService = new GpsProcessingService();
                const stats = gpsService.calculateStats(
                    gpsData.matchTimings,
                    gpsData.geoPoints,
                    gpsData.accuracyThreshold
                ) as GpsStats;
                const sprintsData = gpsService.calculateSprints(
                    gpsData.matchTimings,
                    gpsData.geoPoints
                );
                setCalculatedStats(stats);
                if (sprintMapAccess) {
                    setSprintsData(sprintsData.sprints);
                } else {
                    setSprintsData(exampleMatch.gps.sprints);
                }
            }
        }
    }, [gpsData, match.stats, match.matchFinished, gpsData?.matchTimings]);

    useEffect(() => {
        if (match.matchFinished) {
            if (hrData) {
                if (
                    hrData.heartRateValues &&
                    hrData.heartRateValues.length > 0
                ) {
                    setFilteredHrData(hrData);
                }
            }
        }
    }, [hrData, match.stats, match.matchFinished]);

    // show or hide maps based on gpsData
    function renderMaps() {
        return (
            <>
                {/* Generating distance covered graph for the match*/}
                <ScorzaCard
                    data-testid="fixture-segment-stats"
                    title={t("fixture.FixtureSummaryStats.distance")}
                >
                    <DistanceCoveredComponent
                        match={match}
                        playedSegments={playedSegments}
                        calculatedStats={calculatedStats}
                    />
                </ScorzaCard>

                {/* Generating heatmap for the match*/}
                <ScorzaCard
                    className="card-background"
                    data-testid="heatmap"
                    title={t("general.heatmap")}
                >
                    <IonCardContent>
                        <ProTeaser
                            showTeaser={
                                !heatMapAccess &&
                                match._id !== "example-result1"
                            }
                            teaserName="stats-heatmap"
                        >
                            <GpsMap
                                match={match}
                                mapType={GpsMapType.Heatmap}
                                useDummyData={
                                    !heatMapAccess &&
                                    match._id !== "example-result1"
                                }
                                testId="heatmap"
                            ></GpsMap>
                        </ProTeaser>
                    </IonCardContent>
                </ScorzaCard>

                {/* Generating sprintmap for the match*/}
                <ScorzaCard
                    className="card-background"
                    title={t("fixture.FixtureSummaryStats.sprintmap")}
                >
                    <IonCardContent>
                        <ProTeaser
                            showTeaser={
                                !sprintMapAccess &&
                                match._id !== "example-result1"
                            }
                            teaserName="stats-sprint-map"
                        >
                            <GpsMap
                                match={match}
                                mapType={GpsMapType.SprintMap}
                                sprintsData={sprintsData}
                                useDummyData={
                                    !sprintMapAccess &&
                                    match._id !== "example-result1"
                                }
                                testId="sprintmap"
                            ></GpsMap>
                        </ProTeaser>
                    </IonCardContent>
                </ScorzaCard>
            </>
        );
    }

    function renderHrGraphs() {
        // If user doesn't have data but is a PRO user.
        if (!loading && !hrData) {
            return (
                <ScorzaCard title={t("general.heartRate")}>
                    <IonCardContent>
                        <IonText>
                            {t("fixture.FixtureSummaryStats.errorHRCode2")}
                        </IonText>
                    </IonCardContent>
                </ScorzaCard>
            );
        }

        // Show HeartRate component if we have HR data
        return (
            <ScorzaCard
                className="card-background"
                data-testid="stats-heart-rate-card"
                title={t("general.heartRate")}
            >
                <IonCol>
                    <HeartRateComponent
                        match={match}
                        filteredHrData={filteredHrData}
                        useDummyData={
                            !heartRateAccess && match._id !== "example-result1"
                        }
                    />
                </IonCol>
            </ScorzaCard>
        );
    }

    function renderSpeedCategory() {
        // If user doesn't have data.
        if (!loading && !gpsData) {
            return (
                <ScorzaCard
                    title={t("fixture.FixtureSummaryStats.performanceTracking")}
                >
                    <IonCardContent>
                        <IonText>
                            {`${t(
                                "fixture.FixtureSummaryStats.performanceNotAvailable"
                            )} ${t("fixture.FixtureSummaryStats.errorCode2")}`}
                        </IonText>
                    </IonCardContent>
                </ScorzaCard>
            );
        }

        // Show SpeedCategoryComponent if we have GPSData and user is PRO user.
        return (
            <ScorzaCard className="card-background" title={t("general.speed")}>
                <SpeedCategoryComponent match={match} />
            </ScorzaCard>
        );
    }

    function renderMisconduct() {
        return (
            <ScorzaCard title={t("fixture.FixtureSummaryStats.misconduct")}>
                <IonItem lines="none">
                    <IonGrid>
                        <IonCol>
                            <div className="container-flex">
                                <IonText>{t("general.yellow")}</IonText>
                                <IonText>
                                    {match.stats?.yellowCardTotal}
                                </IonText>
                            </div>

                            {/* Hide sin bin total if number is 0*/}
                            {match.stats?.sinBinsTotal !== 0 && (
                                <IonCol>
                                    <div className="container-flex">
                                        <IonText className="create-match-card-title">
                                            {t(
                                                "fixture.FixtureSummaryStats.sinBins"
                                            )}
                                        </IonText>
                                        <IonText>
                                            {match.stats?.sinBinsTotal}
                                        </IonText>
                                    </div>
                                </IonCol>
                            )}
                        </IonCol>

                        <IonCol>
                            <div className="container-flex">
                                <IonText>{t("general.red")}</IonText>
                                <IonText>{match.stats?.redCardTotal}</IonText>
                            </div>
                        </IonCol>
                    </IonGrid>
                </IonItem>
            </ScorzaCard>
        );
    }

    return (
        <ScrollableContentWithAds>
            {!match.matchFinished ? (
                <ScorzaCard>
                    <IonItem>{notStatsDesc(match, t)}</IonItem>
                </ScorzaCard>
            ) : OfficialRole.fourthOfficial === match.officialRole ||
              OfficialRole.observer === match.officialRole ? (
                <ScorzaCard
                    title={t("fixture.FixtureSummaryStats.performanceTracking")}
                >
                    <IonCardContent className="no-gps-data-card">
                        <IonText>
                            {t("stats.stats.notDataRecorded", {
                                official: match.officialRole,
                            })}
                        </IonText>
                    </IonCardContent>
                </ScorzaCard>
            ) : (
                <>
                    {/*Generate Distance covered Bar if match has GPSData if not we show misconduct screen */}
                    {gpsData ? renderMaps() : null}

                    {/* Generating Heart Rate graphs or show no HR data is available if user has access to view HR data */}
                    {renderHrGraphs()}

                    {/* Generate performance tracking bars if user has GPSData and Access to view the data */}
                    {renderSpeedCategory()}
                </>
            )}

            {developerToolsEnabled && match?._id && (
                <ScorzaCard title={"Debug info"}>
                    <IonCardContent>
                        <MatchDebug
                            match={match}
                            hrData={hrData}
                            gpsData={gpsData}
                        />

                        <FitnessDataItemWrapper matchId={match._id} />
                    </IonCardContent>
                </ScorzaCard>
            )}
        </ScrollableContentWithAds>
    );
};

export default Stats;
